import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  IconChevronRight,
  IconCloudUpload,
  IconDotsVertical,
  IconDownload,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
} from '@rossum/ui/material';
import { DownloadFormat, FORMATS } from '../hooks/useExportDataset';

// An action without a handler is disabled.
type DatasetActionsMenuProps = {
  hasRows: boolean;
  exporting: boolean;
  onExport?: (format: DownloadFormat) => void;
  onReplace?: () => void;
  onDelete?: () => void;
};

export const DatasetActionsMenu = ({
  hasRows,
  exporting,
  onExport,
  onReplace,
  onDelete,
}: DatasetActionsMenuProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(
    null
  );

  const handleClose = () => {
    setExportAnchorEl(null);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        data-cy="dataset-actions-menu-button"
        aria-label={intl.formatMessage({
          id: 'features.datasets.header.moreActions',
        })}
        color="secondary"
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        {exporting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <SvgIcon fontSize="large">
            <IconDotsVertical />
          </SvgIcon>
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{ list: { dense: true } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          data-cy="dataset-actions-export"
          aria-haspopup="menu"
          aria-expanded={Boolean(exportAnchorEl)}
          disabled={!onExport}
          selected={Boolean(exportAnchorEl)}
          onClick={e => setExportAnchorEl(e.currentTarget)}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <IconDownload />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>
            {intl.formatMessage({ id: 'features.datasets.data.export' })}
          </ListItemText>
          <SvgIcon fontSize="small" sx={{ ml: 2, color: 'text.secondary' }}>
            <IconChevronRight />
          </SvgIcon>
        </MenuItem>
        <MenuItem
          data-cy="dataset-actions-replace"
          disabled={!onReplace}
          onClick={() => {
            handleClose();
            onReplace?.();
          }}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <IconCloudUpload />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>
            {intl.formatMessage({
              id: hasRows
                ? 'features.datasets.data.replace'
                : 'features.datasets.data.upload',
            })}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          data-cy="dataset-actions-delete"
          disabled={!onDelete}
          onClick={() => {
            handleClose();
            onDelete?.();
          }}
        >
          <ListItemIcon>
            <SvgIcon fontSize="small">
              <IconTrash />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>
            {intl.formatMessage({
              id: 'features.datasets.header.menu.delete',
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
        slotProps={{ list: { dense: true } }}
        // The actions menu sits at the right edge of the page, so the formats
        // open to its left.
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {FORMATS.map(format => (
          <MenuItem
            key={format}
            data-cy={`dataset-actions-export-${format}`}
            disabled={!onExport}
            onClick={() => {
              handleClose();
              onExport?.(format);
            }}
          >
            {intl.formatMessage({ id: `features.datasets.formats.${format}` })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
